import { Chart } from "chart.js";

// Convert only custom tables inside the cloned element
export const convertCustomTableToTraditionalTable = (
  container: HTMLElement
) => {
  const customTables = container.querySelectorAll(".tc-table");

  customTables.forEach((customTable) => {
    const traditionalTable = document.createElement("table");
    traditionalTable.style.borderCollapse = "collapse";
    traditionalTable.style.width = "100%";

    // Iterate through rows and cells
    customTable.querySelectorAll(".tc-row").forEach((row) => {
      const tr = document.createElement("tr");
      row.querySelectorAll(".tc-cell").forEach((cell) => {
        const td = document.createElement("td");
        td.style.border = "1px solid black";
        td.style.padding = "8px";
        td.innerHTML = cell.innerHTML;
        tr.appendChild(td);
      });
      traditionalTable.appendChild(tr);
    });

    // Replace the custom table with the traditional HTML table
    customTable.replaceWith(traditionalTable);
  });
};

// Convert <div class="ce-delimiter cdx-block"></div> into <hr>
export const convertDivToHr = (container: HTMLElement) => {
  const delimiters = container.querySelectorAll(".ce-delimiter.cdx-block");
  delimiters.forEach((delimiter) => {
    const hr = document.createElement("hr");
    hr.style.margin = "16px 0"; // Optional: Apply some margin to the <hr>
    delimiter.replaceWith(hr);
  });
};

export const convertWarningBlock = (container: HTMLElement) => {
  const warningBlocks = container.querySelectorAll(".cdx-block.cdx-warning");

  warningBlocks.forEach((block) => {
    const warningWrapper = document.createElement("div");
    warningWrapper.style.padding = "10px";
    warningWrapper.style.border = "1px solid #e0c663";
    warningWrapper.style.marginBottom = "16px";

    // Title
    const warningTitle = block.querySelector(".cdx-warning__title");
    if (warningTitle) {
      const titleDiv = document.createElement("div");
      titleDiv.style.fontWeight = "bold";
      titleDiv.style.backgroundColor = "#fff8c4"; // Light yellow background
      titleDiv.style.marginBottom = "6px";
      titleDiv.innerHTML = warningTitle.innerHTML;
      warningWrapper.appendChild(titleDiv);
    }

    // Message
    const warningMessage = block.querySelector(".cdx-warning__message");
    if (warningMessage) {
      const messageDiv = document.createElement("div");
      messageDiv.style.fontSize = "14px"; // Reduced font size
      messageDiv.innerHTML = warningMessage.innerHTML;
      warningWrapper.appendChild(messageDiv);
    }

    // Replace the original block
    block.replaceWith(warningWrapper);
  });
};

export const convertChecklistItems = (container: HTMLElement) => {
  const checklistBlocks = container.querySelectorAll(
    ".cdx-block.cdx-checklist"
  );

  checklistBlocks.forEach((block) => {
    // Create a container with a border to wrap the checklist
    const checklistContainer = document.createElement("div");
    checklistContainer.style.border = "1px solid #cccccc"; // Light gray border
    checklistContainer.style.padding = "10px";
    checklistContainer.style.marginBottom = "16px";
    checklistContainer.style.borderRadius = "5px"; // Optional: Rounded corners

    const checklistItems = block.querySelectorAll(".cdx-checklist__item");

    checklistItems.forEach((item) => {
      const textDiv = item.querySelector(".cdx-checklist__item-text");
      const isChecked = item.classList.contains("cdx-checklist__item--checked");
      const checkmark = isChecked ? "✔️" : "❌"; // Checkmark for checked, Cross for unchecked

      if (textDiv) {
        const newParagraph = document.createElement("p");
        newParagraph.style.fontSize = "16px"; // Optional: Adjust the font size
        newParagraph.style.marginBottom = "8px"; // Optional: Add spacing between items
        newParagraph.innerHTML = `${checkmark} ${textDiv.innerHTML}`;

        // Append each checklist item to the container
        checklistContainer.appendChild(newParagraph);
      }
    });

    // Replace the original checklist block with the new container
    block.replaceWith(checklistContainer);
  });
};

export const removeHiddenChartImages = (container: HTMLElement) => {
  const hiddenImages = container.querySelectorAll(".chart-hidden-image");
  hiddenImages.forEach((img) => img.remove());
};

export const toggleHiddenImagesVisibility = (
  container: HTMLElement,
  visible: boolean
) => {
  const hiddenImages = container.querySelectorAll(".chart-hidden-image");
  hiddenImages.forEach((img) => {
    (img as HTMLElement).style.display = visible ? "block" : "none"; // Make visible for DOCX, hidden afterward
  });
};

export const convertChartsToImagesAndHideThem = async (
  container: HTMLElement
) => {
  // Fetch all canvas elements in the container (or the entire page if container is document)
  const canvases = container.querySelectorAll("canvas");

  for (const canvas of canvases) {
    const chartTitle =
      (canvas.parentElement?.querySelector(".chart-title") as HTMLElement)
        ?.innerText || "Chart";

    await new Promise<void>((resolve) => {
      const chartInstance = Chart.getChart(canvas);
      if (chartInstance) {
        chartInstance.options.animation = {
          ...chartInstance.options.animation,
          onComplete: () => resolve(), // Resolve after rendering completes
        };
        chartInstance.update(); // Trigger re-rendering
      } else {
        resolve(); // Resolve immediately if no chart instance is found
      }
    });

    // Step 2: Convert the visually scaled canvas to a base64 image
    const imageDataURL = canvas.toDataURL("image/png");

    // Step 3: Create an image element with base64 data and hide it on the page
    const img = document.createElement("img");
    img.src = imageDataURL;
    img.alt = chartTitle;
    img.classList.add("chart-hidden-image");
    img.style.display = "none"; // Hidden on live page
    img.setAttribute("img-type", "chart");

    canvas.parentElement?.appendChild(img);
  }
};

export const convertAWSSourcedImages = async (container: HTMLElement) => {
  const imageContainers = container.querySelectorAll(".image-tool--filled");

  for (const container of imageContainers) {
    const img = container.querySelector(".image-tool__image-picture");

    if (img) {
      const imageUrl = img.getAttribute("src");
      const altText = img.getAttribute("alt") || "AWS Image";

      try {
        // Fetch and convert the image to base64 with the correct MIME type
        const blob = await fetch(imageUrl!).then((response) => response.blob());

        const base64Image = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(
              `data:image/png;base64,${(reader.result as string).split(",")[1]}`
            );
          };
          reader.readAsDataURL(blob);
        });

        // Create a new image element with the base64 data
        const newImg = document.createElement("img");
        newImg.src = base64Image;
        newImg.alt = altText;
        newImg.setAttribute("img-type", "aws");

        newImg.style.display = "none"; // Keep it hidden

        // Ensure proper dimensions
        newImg.onload = () => {
          const width = img.getAttribute("width");
          if (width) {
            newImg.setAttribute("aws-width", width);
          }
          newImg.setAttribute(
            "aws-height",
            img.getAttribute("height") || "auto"
          );
        };

        // If the image is already loaded (cache), trigger the onload event manually
        if (newImg.complete) {
          newImg.onload?.(new Event("load"));
        }

        // Append the new image next to the original one
        img.insertAdjacentElement("afterend", newImg);
      } catch (error) {
        console.error("Error converting AWS image:", error);
      }
    }
  }
};

export const convertImagesToDecentSize = (container: HTMLElement): void => {
  const images = container.querySelectorAll("img");

  images.forEach((img) => {
    const updateImageSize = () => {
      // Define Word document constraints (in pixels for 96 DPI)
      const maxDocWidth = 624; // ~6.5 inches
      const maxDocHeight = 864; // ~9 inches

      // Use natural dimensions by default
      let finalWidth = img.naturalWidth;
      let finalHeight = img.naturalHeight;

      // Check if new-width and new-height attributes exist and use them
      const newWidthAttr = img.getAttribute("aws-width");
      const newHeightAttr = img.getAttribute("aws-height");

      if (newWidthAttr && newHeightAttr) {
        let newWidth = parseInt(newWidthAttr, 10);
        let newHeight = parseInt(newHeightAttr, 10);

        // Ensure the new dimensions don't exceed document constraints
        if (newWidth > maxDocWidth) {
          const scaleFactor = maxDocWidth / newWidth;
          newWidth = maxDocWidth;
          newHeight *= scaleFactor;
        }

        if (newHeight > maxDocHeight) {
          const scaleFactor = maxDocHeight / newHeight;
          newHeight = maxDocHeight;
          newWidth *= scaleFactor;
        }

        finalWidth = Math.round(newWidth);
        finalHeight = Math.round(newHeight);
      } else {
        // Ensure the natural dimensions don't exceed document constraints
        if (finalWidth > maxDocWidth) {
          const scaleFactor = maxDocWidth / finalWidth;
          finalWidth = maxDocWidth;
          finalHeight *= scaleFactor;
        }

        if (finalHeight > maxDocHeight) {
          const scaleFactor = maxDocHeight / finalHeight;
          finalHeight = maxDocHeight;
          finalWidth *= scaleFactor;
        }
      }

      // Set the final width and height
      img.setAttribute("width", `${finalWidth}`);
      img.setAttribute("height", `${finalHeight}`);

      // Apply styles for spacing and fit
      img.style.marginBottom = "10px";
      img.style.objectFit = "contain";
    };

    // Assign event listener for image load
    img.onload = updateImageSize;

    // Handle cached images
    if (img.complete) {
      updateImageSize();
    }
  });
};

const removeAllCanvasElements = (container: HTMLElement) => {
  const canvases = container.querySelectorAll("canvas");

  canvases.forEach((canvas) => {
    canvas.remove();
  });
};

const removeUnwantedDivs = (container: HTMLElement) => {
  const unwantedDivs = container.querySelectorAll(
    ".codex-editor-overlay, .ce-inline-toolbar"
  );

  unwantedDivs.forEach((div) => div.remove());
};

const replaceImageContainersWithAWSImage = (container: HTMLElement) => {
  const imageContainers = container.querySelectorAll(
    ".cdx-block.image-tool.image-tool--filled"
  );

  imageContainers.forEach((imageContainer) => {
    const awsImage = imageContainer.querySelector(
      'img[img-type="aws"]'
    ) as HTMLImageElement;
    if (!awsImage) return;

    // Capture the caption text before replacing the container
    const captionElement = imageContainer.querySelector(".image-caption");
    const captionText = captionElement
      ? captionElement.textContent?.trim() || ""
      : "";

    // Create a new image element with the same attributes
    const newImage = document.createElement("img");
    newImage.src = awsImage.src;
    newImage.alt = awsImage.alt || "AWS Image";
    newImage.width = awsImage.width;
    newImage.height = awsImage.height;
    newImage.setAttribute("img-type", "aws");

    // Create a new caption <p> element with the captured text
    const caption = document.createElement("p");
    caption.textContent = captionText;
    caption.classList.add("text-gray-700", "italic", "text-center", "mt-2");

    // Create a wrapper div to maintain structure
    const wrapper = document.createElement("div");
    wrapper.classList.add("cdx-block", "image-tool", "image-tool--filled");

    // Append the image and caption to the wrapper
    wrapper.appendChild(newImage);
    if (captionText) {
      wrapper.appendChild(caption);
    }

    // Replace the original container with the new wrapped content
    imageContainer.replaceWith(wrapper);
  });
};

export const removeImagesWithImgType = (container: HTMLElement) => {
  const images = container.querySelectorAll("img[img-type]");
  images.forEach((image) => image.remove());
};

export const formatDocx = (clonedElement: HTMLElement) => {
  toggleHiddenImagesVisibility(clonedElement, true);

  convertCustomTableToTraditionalTable(clonedElement);

  convertDivToHr(clonedElement);

  convertWarningBlock(clonedElement);

  convertChecklistItems(clonedElement);

  convertImagesToDecentSize(clonedElement);

  removeAllCanvasElements(clonedElement);

  removeUnwantedDivs(clonedElement);

  replaceImageContainersWithAWSImage(clonedElement);
};
